import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DocSignService,
  EgibObject,
  FormAlertService,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  PortalId,
  PzService,
  StreetService,
  ToolType,
  ibanErrorName,
  markFormGroupsTouched,
} from '@gk/gk-modules';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { ProxyDetailsComponent } from '../../../components/proxy-details/proxy-details.component';
import { RequestInProgress } from '../../../guards/request-in-progress/request-in-progress.guard';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { DateRangePickerComponent } from '../../../shared/date-range-picker/date-range-picker.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import {
  DateHelperUtil,
  NgbDateRange,
} from '../../../utils/date-helper/date-helper.util';
import { MCGRFinancialSectorApplicationFormArrayComponent } from '../financial-sector-application-form-array/financial-sector-application-form-array.component';
import { MCGROtherConservationActivitiesFormArrayComponent } from '../other-conservation-activities-form-array/other-conservation-activities-form-array.component';
import { MCGRPermitSectionComponent } from '../permit-section/permit-section.component';
import {
  MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName,
  MCGRNewRequestControlName,
  MCGRNewRequestFormGroup,
} from '../services/new-request-form/new-request-form.model';
import { MCGRNewRequestFormService } from '../services/new-request-form/new-request-form.service';
import {
  MCGRNewRequest,
  MCGRNewRequestDto,
  MCGRNewRequestFormWorkspaceState,
} from '../services/new-request/new-request.model';
import { MCGREstimatedWorksCostsFormGroup } from '../works-estimated-costs/works-estimated-costs-form.model';
import { MCGRWorksEstimatedCostsComponent } from '../works-estimated-costs/works-estimated-costs.component';
import { MainRoutes } from './../../../guards/guard.models';

@Component({
  selector: 'app-mcgr-new-request',
  templateUrl: './new-request.component.html',
  standalone: false,
})
export class MCGRNewRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy, RequestInProgress
{
  override controlName = MCGRNewRequestControlName;
  landAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName =
    MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName;
  override formGroup: FormGroup<MCGRNewRequestFormGroup>;
  override portalId = PortalId.MonumentConservationGrantRequest;
  readonly mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('districtName', 'PARCEL_SEARCH_FORM.DISTRICT'),
      new ColumnHeader('mapObjectNumber', 'GK.MAP.PARCEL_OR_BUILDING_NUMBER'),
      new ColumnHeader('address', 'GK.MAP.ADDRESS'),
      new ColumnHeader('mapSheet', 'PARCEL_SEARCH_FORM.SHEET'),
      new ColumnHeader('typeName', 'GK.MAP.TYPE'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    false,
    true,
    '25',
  );
  mapSettings: MapSettings;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  @ViewChild('canDeactivateModal') canDeactivateModal: NgbModalRef;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  @ViewChild('worksDateRangeComponent')
  worksDateRangeComponent: DateRangePickerComponent;
  @ViewChild(MCGRFinancialSectorApplicationFormArrayComponent)
  mcgrFinancialSectorApplicationFormArrayComponent: MCGRFinancialSectorApplicationFormArrayComponent;
  @ViewChild(MCGRWorksEstimatedCostsComponent)
  mcgrWorksEstimatedCostsComponent: MCGRWorksEstimatedCostsComponent;
  @ViewChild(MCGROtherConservationActivitiesFormArrayComponent)
  mcgrOtherConservationActivitiesFormArrayComponent: MCGROtherConservationActivitiesFormArrayComponent;
  @ViewChild('constructionPermitSection')
  constructionPermitSection: MCGRPermitSectionComponent;
  successSigned = false;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.MonumentConservationGrantRequestFormVersion;
  workspaceStateId = WorkspaceStateId.MonumentConservationGrantRequestForm;
  otherFileRequired = false;

  constructor(
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    private mcgrNewRequestFormService: MCGRNewRequestFormService,
    public override streetService: StreetService,
    private toastr: ToastrService,
    public override router: Router,
    private modalService: NgbModal,
    public override pzService: PzService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
    );
  }

  override ngOnInit(): void {
    this.fetchWrongFileText();
    this.subscribeToDocSignTranslations();
    this.createForm();
    this.fetchMapSettings();
  }

  createForm(): void {
    this.formGroup = this.mcgrNewRequestFormService.getFormGroup();
  }

  fetchMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.Applicant)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.mapSettings = mapSettings;
        this.setMapState();
      });
  }

  override setMapState(): void {
    this.mapState = new MapState(
      MapId.MonumentConservationGrantRequest,
      this.mapStateService.getViewState(
        MapId.MonumentConservationGrantRequest,
        this.mapSettings,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel, ToolType.Building],
        this.mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.MonumentConservationGrantRequest,
        this.mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.isRequestDirty() && !this.successSigned
      ? from(
          this.modalService.open(this.canDeactivateModal, {
            beforeDismiss: () => false,
          }).result,
        )
      : true;
  }

  isRequestDirty(): boolean {
    return (
      this.formGroup.dirty || this.attachmentsComponent.areChosenDocuments()
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.attachmentsComponent.areDocumentsValid() &&
      this.isMapSectionValid()
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getInvestorDetailsFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  sendRequest(): void {
    this.getRequestBody()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/interesant/wniosek/dotacjeNaKonserwacjeZabytku/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: (error) => this.handleSendAndValidateFailure(error),
      });
  }

  getRequestBody = (): Observable<MCGRNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;
          this.toastr.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        MCGRNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getLandParcels(),
          this.getBuildings(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave = (): MCGRNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
    mapState: this.mapState,
  });

  handleLoadWorkspaceResponse(state: MCGRNewRequestFormWorkspaceState): void {
    if (state.formValue?.worksDateRange) {
      this.worksDateRangeComponent.setPeriod(state.formValue?.worksDateRange);
    }
    this.mcgrFinancialSectorApplicationFormArrayComponent.createFormArrayFromData(
      state.formValue?.financialSectorApplication,
    );
    this.mcgrWorksEstimatedCostsComponent.createFormArrayFromData(
      state.formValue?.worksEstimatedCosts?.otherFunds,
    );
    this.mcgrOtherConservationActivitiesFormArrayComponent.createFormArrayFromData(
      state.formValue?.otherConservationActivities,
    );
    this.constructionPermitSection.setFormFromData(
      state.formValue?.constructionPermit,
    );
    this.formGroup.patchValue({
      ...state.formValue,
      investorDetails: {
        ...state.formValue.investorDetails,
        addressSelect: undefined,
      },
      constructionPermit: undefined,
    });
    if (state.formValue?.investorDetails?.addressSelect) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
    }
    if (state.formValue?.proxyDetailsArray) {
      this.proxyDetailsComponent.setFormGroupValue(
        state.formValue.proxyDetailsArray,
      );
    }
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    this.mapState = state.mapState;
  }

  getLandParcels(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  getBuildings(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.Building)
      ),
    );
  }

  getFormValue(): MCGRNewRequest {
    return this.formGroup.getRawValue();
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.MonumentConservationGrantRequest}/requests-list`,
    );
  }

  isMapSectionValid(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.length > 0;
  }

  isTaskNameFormControlValid(): boolean {
    return this.getTaskNameFormControl().valid;
  }

  getTaskNameFormControl(): FormControl {
    return this.formGroup.get(this.controlName.TaskName) as FormControl;
  }

  isMonumentNameFormControlValid(): boolean {
    return this.getMonumentNameFormControl().valid;
  }

  getMonumentNameFormControl(): FormControl {
    return this.formGroup.get(this.controlName.MonumentName) as FormControl;
  }

  isMonumentRegistryNumberFormControlValid(): boolean {
    return this.getMonumentRegistryNumberFormControl().valid;
  }

  getMonumentRegistryNumberFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.MonumentRegistryNumber,
    ) as FormControl;
  }

  isJustificationOfImportanceFormControlValid(): boolean {
    return this.getJustificationOfImportanceFormControl().valid;
  }

  getJustificationOfImportanceFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.JustificationOfImportance,
    ) as FormControl;
  }

  isContactPersonFormControlValid(): boolean {
    return this.getContactPersonFormControl().valid;
  }

  getContactPersonFormControl(): FormControl {
    return this.formGroup.get(this.controlName.ContactPerson) as FormControl;
  }

  shouldShowApplicantAccountNumberFormControlRequiredError(): boolean {
    const control = this.getApplicantAccountNumberFormControl();

    return this.submitted && this.shouldShowRequiredAlert(control);
  }

  shouldShowApplicantAccountNumberFormControlIbanError(): boolean {
    const control = this.getApplicantAccountNumberFormControl();
    return control.touched && control.errors?.[ibanErrorName] && !control.valid;
  }

  getApplicantAccountNumberFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.ApplicantAccountNumber,
    ) as FormControl;
  }

  isLegalTitleToMonumentFormControlValid(): boolean {
    return this.getLegalTitleToMonumentFormControl().valid;
  }

  getLegalTitleToMonumentFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.LegalTitleToMonument,
    ) as FormControl;
  }

  isLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormGroupValid(): boolean {
    return this.getLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormGroup()
      .valid;
  }

  getLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormGroup(): FormGroup {
    return this.formGroup.get(
      this.controlName
        .LandAndMortgageRegisterNumberAndDocumentsCollectionNumber,
    ) as FormGroup;
  }

  getConservationPermitFormGroup(): FormGroup {
    return this.formGroup.get(this.controlName.ConservationPermit) as FormGroup;
  }

  getConstructionPermitFormGroup(): FormGroup {
    return this.formGroup.get(this.controlName.ConstructionPermit) as FormGroup;
  }

  isScopeOfWorksFormControlValid(): boolean {
    return this.getScopeOfWorksFormControl().valid;
  }

  getScopeOfWorksFormControl(): FormControl {
    return this.formGroup.get(this.controlName.ScopeOfWorks) as FormControl;
  }

  isJustificationOfWorksPurposeFormControlValid(): boolean {
    return this.getJustificationOfWorksPurposeFormControl().valid;
  }

  getJustificationOfWorksPurposeFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.JustificationOfWorksPurpose,
    ) as FormControl;
  }

  updateWorksDateRangeFormControl(dateRange: NgbDateRange): void {
    this.getWorksDateFormControl().setValue(dateRange);
  }

  isWorksDateRangeInvalid(): boolean {
    return !this.isWorksDateRangeCorrect() || this.isWorksDateRangeEmpty();
  }

  isWorksDateRangeCorrect(): boolean {
    const formControlValue = this.getWorksDateFormControl().value || {};

    return DateHelperUtil.isToDateNotLessThanFromDate(
      formControlValue.from,
      formControlValue.to,
    );
  }

  isWorksDateRangeEmpty(): boolean {
    const formControlValue = this.getWorksDateFormControl().value || {};

    return _.isEmpty(formControlValue.from) && _.isEmpty(formControlValue.to);
  }

  getWorksDateFormControl(): FormControl<NgbDateRange> {
    return this.formGroup.get(
      this.controlName.WorksDateRange,
    ) as FormControl<NgbDateRange>;
  }

  getWorksEstimatedCostsFormGroup(): FormGroup<MCGREstimatedWorksCostsFormGroup> {
    return this.formGroup.get(
      this.controlName.WorksEstimatedCosts,
    ) as FormGroup<MCGREstimatedWorksCostsFormGroup>;
  }

  isExpenditureDeclarationFormControlValid(): boolean {
    return this.getExpenditureDeclarationFormControl().valid;
  }

  getExpenditureDeclarationFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.ExpenditureDeclaration,
    ) as FormControl;
  }

  isAwarenessAndTruthfulnessDeclarationFormControlValid(): boolean {
    return this.getAwarenessAndTruthfulnessDeclarationFormControl().valid;
  }

  getAwarenessAndTruthfulnessDeclarationFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.AwarenessAndTruthfulnessDeclaration,
    ) as FormControl;
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
